import React, { useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { GoogleAdsLinker } from './index';
import { EmailConfirmation } from './EmailConfirmation';
import { PollingSuccess } from './PollingSuccess';
import { PollingTimeout } from './PollingTimeout';
import { PollingError } from './PollingError';
import { HealthCheckError } from './HealthCheckError';
import { usePolling } from '@/hooks/usePolling';
import { googleAdsApi, HealthCheckResult } from '@/services/api/googleAds';
import { useInvalidateLinkageEligibility } from '@/hooks/queries/useLinkageEligibilityQuery';
import { debug } from '@/utils/debug';
import { ErrorBoundary } from '@/components/common/ErrorBoundary';

// Type for the polling success result (subset of GoogleAdsSetupResponse)
interface PollingSuccessResult {
  campaigns_found?: number;
  total_budget?: string;
  account_name?: string;
}

type LinkingStep = 'modal' | 'email' | 'success' | 'timeout' | 'error' | 'health_error';

interface PollingManagerProps {
  isOpen: boolean;
  onClose: () => void;
  onAccountLinked?: () => Promise<void>; // Callback to refresh account data
}

export function PollingManager({ isOpen, onClose, onAccountLinked }: PollingManagerProps) {
  const [currentStep, setCurrentStep] = useState<LinkingStep>('modal');
  const [customerId, setCustomerId] = useState<string>('');
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [setupResult, setSetupResult] = useState<PollingSuccessResult | null>(null);
  const [isRefreshingData, setIsRefreshingData] = useState(false);
  const [healthCheckResult, setHealthCheckResult] = useState<HealthCheckResult | null>(null);
  // True when the most recent error came from an explicit server-side
  // eligibility rejection (e.g. the tier aggregate gate at /complete-setup
  // returning success=false). PollingError uses this to switch its title
  // to "Account Not Eligible" and suppress generic troubleshooting steps.
  // Distinct from terminalErrorRef (which is a one-bit handshake to block
  // the success modal from firing) because we want to know *why* the error
  // landed, not just that one did.
  const [isIneligible, setIsIneligible] = useState(false);
  // Set inside the poll function when the request terminated with a server-side
  // rejection (e.g. the tier aggregate gate). usePolling treats any truthy
  // return as "success" and invokes onSuccess, which would otherwise overwrite
  // the error state we just set. This ref is the one-bit handshake that tells
  // onSuccess to bail out without rendering the success modal/toast.
  const terminalErrorRef = useRef(false);
  const navigate = useNavigate();
  const invalidateEligibility = useInvalidateLinkageEligibility();

  // Polling configuration
  const pollingConfig = {
    onSuccess: async (result: unknown) => {
      // The poll function already set currentStep='error' and the user-facing
      // message. Do not overwrite with the success render.
      if (terminalErrorRef.current) {
        terminalErrorRef.current = false;
        debug.log('PollingManager: onSuccess skipped — poll function reported a terminal rejection');
        return;
      }
      debug.log('PollingManager: Polling succeeded', result);

      // isRefreshingData is already true from the setup phase
      // Keep showing the same setup message while refreshing account data
      if (onAccountLinked) {
        updateStatusMessage('Account linked! Please hold tight for a few minutes as we setup your account...');
        try {
          await onAccountLinked();
          debug.log('PollingManager: Account data refreshed successfully');
        } catch (error) {
          debug.error('PollingManager: Failed to refresh account data', error);
        } finally {
          // Now we can reset the flag
          setIsRefreshingData(false);
        }
      }

      // Now transition to success after data is refreshed
      setCurrentStep('success');
      toast.success('Google Ads account connected successfully!');
    },
    onTimeout: (customerIdValue: string) => {
      debug.log('PollingManager: Polling timed out for', customerIdValue);
      setCurrentStep('timeout');
    },
    onError: (error: string) => {
      debug.error('PollingManager: Polling error', error);
      setErrorMessage(error);
      setCurrentStep('error');
    },
    customerId,
  };

  // Initialize polling hook first
  const {
    state: pollingState,
    startPolling,
    stopPolling,
    manualCheck,
    updateStatusMessage,
  } = usePolling(async (customerIdToCheck: string): Promise<boolean> => {
    try {
      debug.log('PollingManager: Checking link status for', customerIdToCheck);
      
      // First check the link status
      const statusResult = await googleAdsApi.getLinkStatus(customerIdToCheck);
      
      debug.log('PollingManager: Link status result', statusResult);
      debug.log('PollingManager: Status check details', {
        is_linked: statusResult.is_linked,
        link_status: statusResult.link_status,
        condition1: statusResult.is_linked,
        condition2: statusResult.link_status === 'ACTIVE',
        bothConditions: statusResult.is_linked && statusResult.link_status === 'ACTIVE'
      });

      if (statusResult.is_linked && statusResult.link_status === 'ACTIVE') {
        // Account is linked! Complete setup immediately
        debug.log('PollingManager: Account is linked and active, completing setup');

        // Update UI to show setup is in progress
        setIsRefreshingData(true);
        updateStatusMessage('Account linked! Please hold tight for a few minutes as we setup your account...');

        try {
          const completionResult = await googleAdsApi.completeSetup(customerIdToCheck);
          debug.log('PollingManager: Setup completion response', completionResult);

          // The backend's aggregate-threshold gate returns 200 with success=false
          // when a link would put the member below tier threshold (and reverts
          // the MCC link). Surface the server's message instead of dropping
          // into the generic "Unexpected Error" path. terminalErrorRef tells
          // usePolling's onSuccess (which fires next, because we return true)
          // not to overwrite the error state with the success render.
          if (completionResult.success === false) {
            terminalErrorRef.current = true;
            setIsRefreshingData(false);
            setErrorMessage(completionResult.message || 'This account could not be linked.');
            setIsIneligible(true);
            setCurrentStep('error');
            // Toast is the brief notification cue; the modal carries the full
            // server message. Keep the toast short to avoid duplicating the
            // same wall of text in two places on screen.
            toast.error('Account link ineligible');
            return true; // Stop polling — this is a terminal rejection, not a transient failure.
          }

          // Convert GoogleAdsSetupResponse to PollingSuccessResult
          setSetupResult(completionResult as PollingSuccessResult);
          // Credential row for this member was just created (or reactivated)
          // by the backend during complete-setup — flip the eligibility
          // cache so the "Link Meta" button hides (free tier) without
          // waiting for the 2-minute staleTime to lapse.
          void invalidateEligibility();
          // Keep isRefreshingData true - it will be reset in onSuccess after final refresh
          return true; // Success!
        } catch (setupError) {
          debug.error('PollingManager: Setup completion failed', setupError);
          setIsRefreshingData(false);
          // Setup failed - show error but don't continue polling
          let errorMessage = 'Unknown error occurred';

          if (setupError instanceof Error) {
            if (setupError.message.includes('timeout')) {
              errorMessage = 'Account setup is taking longer than expected. The account has been linked successfully, but data import may still be in progress. Please check the campaigns page in a few minutes.';
            } else {
              errorMessage = setupError.message;
            }
          } else {
            errorMessage = String(setupError);
          }

          terminalErrorRef.current = true;
          setErrorMessage(`Setup failed: ${errorMessage}`);
          setCurrentStep('error');
          return true; // Stop polling (we got an error, not a continue-polling scenario)
        }
      } else {
        // Still waiting for acceptance
        debug.log('PollingManager: Still waiting for invitation acceptance', {
          linkStatus: statusResult.link_status,
          isLinked: statusResult.is_linked,
        });
        return false; // Continue polling
      }
    } catch (error) {
      debug.warn('PollingManager: Network error during status check', error);
      // Network errors should continue polling (transient failures)
      throw error;
    }
  }, pollingConfig);

  // Handle customer ID submission and start linking process
  const handleConnect = useCallback(async (customerIdValue: string) => {
    try {
      debug.log('PollingManager: Starting connection process for', customerIdValue);
      setCustomerId(customerIdValue);

      // Send invitation to link account (includes health check)
      const linkResult = await googleAdsApi.linkAccount({ customer_id: customerIdValue });
      debug.log('PollingManager: Link account response', linkResult);

      // Handle health check failure (account not healthy enough to link)
      if (!linkResult.success && linkResult.health_check) {
        debug.log('PollingManager: Health check failed', linkResult.health_check);
        setHealthCheckResult(linkResult.health_check);
        setErrorMessage(linkResult.message);
        setCurrentStep('health_error');
        toast.error('Account cannot be linked due to policy issues');
        return;
      }

      // Handle other failure cases (no health_check but success is false)
      if (!linkResult.success) {
        debug.log('PollingManager: Link failed without health check', linkResult);
        setErrorMessage(linkResult.message || 'Failed to link account');
        setCurrentStep('error');
        toast.error(linkResult.message || 'Failed to link account');
        return;
      }

      // Link successful - show email confirmation and start polling
      debug.log('PollingManager: Link successful, setting currentStep to email');
      setCurrentStep('email');
      debug.log('PollingManager: Email step should now be visible');

      // Start the adaptive polling system after invitation is sent
      if (startPolling(customerIdValue)) {
        debug.log('PollingManager: Polling started successfully');
        toast.success('Invitation sent! Check your email.');
      } else {
        throw new Error('Failed to start polling session');
      }

    } catch (error) {
      debug.error('PollingManager: Connection failed', error);

      // Extract error message from axios error response or Error object
      let errorData: string;
      const axiosError = error as { response?: { data?: { detail?: string } } };
      if (axiosError.response?.data?.detail) {
        // API returned a specific error message
        errorData = axiosError.response.data.detail;
      } else if (error instanceof Error) {
        errorData = error.message;
      } else {
        errorData = String(error);
      }

      setErrorMessage(errorData);
      setCurrentStep('error');
      toast.error(errorData);
    }
  }, [startPolling]);

  // Handle manual check
  const handleManualCheck = useCallback(() => {
    manualCheck(customerId);
  }, [manualCheck, customerId]);

  // Handle go home
  const handleGoHome = useCallback(() => {
    stopPolling();
    onClose();
    navigate('/dashboard');
  }, [stopPolling, onClose, navigate]);

  // Handle view campaigns (for successful link with timeout error)
  const handleViewCampaigns = useCallback(() => {
    stopPolling();
    onClose();
    navigate('/account');
  }, [stopPolling, onClose, navigate]);

  // Handle retry from error state
  const handleRetry = useCallback(() => {
    terminalErrorRef.current = false;
    setIsIneligible(false);
    setCurrentStep('modal');
    setErrorMessage('');
    setCustomerId('');
  }, []);

  // Handle resume polling from timeout
  const handleResumePolling = useCallback(() => {
    if (customerId) {
      startPolling(customerId);
      setCurrentStep('email');
    }
  }, [customerId, startPolling]);

  // Handle close
  const handleClose = useCallback(() => {
    stopPolling();
    setCurrentStep('modal');
    setCustomerId('');
    setErrorMessage('');
    setSetupResult(null);
    setHealthCheckResult(null);
    setIsIneligible(false);
    onClose();
  }, [stopPolling, onClose]);

  // Reset state when modal is opened
  React.useEffect(() => {
    if (isOpen) {
      debug.log('PollingManager: Modal opened, resetting state');
      stopPolling();
      setCurrentStep('modal');
      setCustomerId('');
      setErrorMessage('');
      setSetupResult(null);
      setHealthCheckResult(null);
      setIsIneligible(false);
    }
  }, [isOpen, stopPolling]);

  if (!isOpen) return null;

  debug.log('PollingManager: Rendering with state', { 
    currentStep, 
    customerId, 
    pollingStatus: pollingState.status,
    isPolling: pollingState.isPolling 
  });

  const renderStep = () => {
    switch (currentStep) {
      case 'modal':
        return (
          <GoogleAdsLinker
            isOpen={true}
            onClose={handleClose}
            onConnect={handleConnect}
            isConnecting={pollingState.status === 'starting'}
          />
        );

      case 'email':
        debug.log('PollingManager: Rendering EmailConfirmation component', {
          customerId,
          isPolling: pollingState.isPolling,
          totalAttempts: pollingState.totalAttempts,
          phase: pollingState.phase
        });
        return (
          <EmailConfirmation
            customerId={customerId}
            isPolling={pollingState.isPolling}
            pollingAttempts={pollingState.totalAttempts}
            pollingPhase={pollingState.phase}
            pollingStatus={pollingState.statusMessage}
            isRefreshingData={isRefreshingData}
            onGoHome={handleGoHome}
          />
        );

      case 'success':
        return (
          <PollingSuccess
            customerId={customerId}
            result={setupResult}
            onGoHome={handleGoHome}
          />
        );

      case 'timeout':
        return (
          <PollingTimeout
            customerId={customerId}
            onManualCheck={handleManualCheck}
            onResumePolling={handleResumePolling}
            onGoHome={handleGoHome}
          />
        );

      case 'error':
        return (
          <PollingError
            customerId={customerId}
            errorMessage={errorMessage}
            isIneligible={isIneligible}
            onRetry={handleRetry}
            onGoHome={handleGoHome}
            onViewCampaigns={handleViewCampaigns}
          />
        );

      case 'health_error':
        return (
          <HealthCheckError
            customerId={customerId}
            healthLevel={healthCheckResult?.health_level || 'unknown'}
            message={errorMessage}
            onRetry={handleRetry}
            onClose={handleClose}
          />
        );

      default:
        return null;
    }
  };

  return <ErrorBoundary>{renderStep()}</ErrorBoundary>;
}